import {Edge, Node} from 'reactflow';

import {GetAnalysisDataFrame, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {MakeAllFieldsRequired} from 'util/Types';

/**
 * A node in the analysis graph.
 *
 * Currently, every node represents a step in the pipeline.
 */
export enum AnalysisGraphNodeType {
  STEP = 'STEP',
}

/**
 * An edge in the analysis graph.
 *
 * Currently, every edge represents data frames or files flowing between steps.
 */
export enum AnalysisGraphEdgeType {
  EDGE = 'EDGE',
}

/**
 * The analysis-specific data associated with a node in the analysis flowchart graph
 */
export interface AnalysisGraphNodeData {
  step: GetAnalysisPipelineStep;
  isTemplate: boolean;
  /**
   * Handles of edges coming into the node. Each handle represents an input step data.
   */
  targetHandleIds: Array<string>;
  /**
   * Handles of edges leaving the node. Each handle represents an output step data.
   */
  sourceHandleIds: Array<string>;
}

/**
 * A node in the analysis flowchart graph, using the interface expected by `reactflow`.
 */
export type AnalysisGraphNode = Pick<Node<AnalysisGraphNodeData>, 'id' | 'position' | 'data' | 'type'> & {
  type: AnalysisGraphNodeType;
};

/**
 * Information used to create AnalysisGraphEdgeData.
 *
 * This is kept separate from `AnalysisGraphEdgeData`, which only tracks the information necessary to render the
 * edge. This is to discourage edge components from being concerned about their nodes.
 *
 * @package
 */
export interface DraftAnalysisGraphEdgeData {
  parent: GetAnalysisPipelineStep;
  child: GetAnalysisPipelineStep;
  parentStepDataName: string;
  childStepDataName: string;
}

/**
 * The analysis-specific data associated with an edge in the analysis flowchart graph
 */
export interface AnalysisGraphEdgeData {
  /**
   * The data frames passed along the edge, between steps.
   *
   * This can be empty if the parent step has not succeeded.
   */
  dataFrames: Array<GetAnalysisDataFrame>;
  /**
   * True if this edge is the first occurence of a data frame's name.
   *
   * (Or, alternately, the parent step doesn't have an input data frame of the same name.)
   */
  originatesDataFrameName: boolean;
}

/**
 * An edge in the analysis flowchart graph, using the interface expected by `reactflow`
 */
export type AnalysisGraphEdge = MakeAllFieldsRequired<
  Pick<
    Edge<AnalysisGraphEdgeData>,
    'id' | 'source' | 'target' | 'data' | 'type' | 'sourceHandle' | 'targetHandle'
  >
> & {
  type: AnalysisGraphEdgeType;
};

/**
 * The analysis flowchart graph, the data structure on which `AnalysisFlowchart` is built.
 */
export interface AnalysisGraph {
  nodes: Array<AnalysisGraphNode>;
  edges: Array<AnalysisGraphEdge>;
}
